import type { ForwardRefRenderFunction, RefObject } from "react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { isNil } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { setPrimarySidebarType, togglePrimarySidebar } from "app/actions/userData/primarySidebar3Actions";
import { swSettings } from "app/common/services/swSettings";
import { isAeoSolution } from "app/common/utils/isAeoSolution";
import { isAppSolution, isTrialPlan, redirectToAppsSolution } from "app/common/utils/isAppSolution";
import { getUiBranch, getUiVersion } from "app/components/ProUIVersionIndication/ProUIVersionIndication";
import { AiStudioButton } from "app/components/React/AiStudioButton/AiStudioButton";
import { CueSidebarButton } from "app/components/React/CueSidebarButton/CueSidebarButton";
import { QuickSearch } from "app/components/React/QuickSearch/QuickSearch";
import { SuperAgentSidebarButton } from "app/components/React/SuperAgentSidebarButton/SuperAgentSidebarButton";
import { getSidebarConfig } from "app/components/sidebar3/dynamic-config/getSidebarConfig";
import { SolutionSwitcherDropdown } from "app/components/sidebar3/primary-side-bar/secondary-actions/Solutions/SolutionSwitcherDropdown";
import {
    BottomComponentsWrapper,
    BottomMenuComponentsWrapper,
    EFadePlacement,
    GroupMenuComponentsWrapper,
    HeaderWrapper,
    PrimarySidebarWrapper,
    ScrollableTopComponentsWrapper,
    SecondaryItemsWrapper,
    SecondaryMiddleItemsWrapper,
    SWLogoWrapper,
    TopMenuComponentsWrapper,
    WarningsWrapper,
} from "app/components/sidebar3/primary-side-bar/styledComponents";
import {
    getInitialSubsectionId,
    getSidebarItemsComponents,
    getSolutionLogoProps,
    getWarningComponents,
    isSidebarAlwaysOpenedOnPage,
} from "app/components/sidebar3/primary-side-bar/utils";
import { sidebarTrackingDispatcher } from "app/components/sidebar3/secondary-side-bar/components/DigitalSuiteNavBarUtils";
import { ClickOutsideWrapper } from "app/custom-hooks/useClickOutside";
import { useIsCursorOver } from "app/custom-hooks/useIsCursorOver";
import useLatestValue from "app/custom-hooks/useLatestValue";
import { ACTIVATION_HOME_ROUTE } from "app/pages/activation/routing/constants";
import { EPrimarySideBar3IdType, EPrimarySideBar3StateType } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import type { ISubscriptionSolution } from "app/user-data/solutionBaseInterfaces";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { SolutionLogo } from "./SolutionLogo";
import { allTrackers } from "app/services/track/track";

const DASHBOARD_NAME_FROM_STATE = "dashboard-exist";
const ALLOWED_VIEWER_ITEMS: Array<EPrimarySideBar3IdType> = [
    EPrimarySideBar3IdType.Dashboards,
    EPrimarySideBar3IdType.Settings,
];
const DEFAULT_HEADER_HEIGHT = 56;

interface Props {
    showQuickSearch?: boolean;
    isQuickSearchLocked?: boolean;
    showAiStudio?: boolean;
    cueSubscription?: ISubscriptionSolution;
    showSuperAgentButton?: boolean;
    sidebarContainerRef: RefObject<HTMLElement | null>;
}

const PrimarySideBarRenderFunction: ForwardRefRenderFunction<
    {
        onMouseLeavePrimarySidebar: VoidFunction;
    },
    Props
> = (
    { showQuickSearch, isQuickSearchLocked, showAiStudio, cueSubscription, showSuperAgentButton, sidebarContainerRef },
    ref,
) => {
    const {
        impersonateMode,
        isPrimarySidebarOpened,
        isUserSettingsOpened,
        isKeepSidebarOpened,
        primaryBarData,
        SecondarySideBar3Type,
        isOnMobile,
    } = useSelector((state) => {
        return {
            impersonateMode: state.impersonation.impersonateMode,
            isPrimarySidebarOpened: state.primaryBar?.isPrimarySidebarOpened,
            isUserSettingsOpened: state.primaryBar?.isUserSettingsOpened,
            isKeepSidebarOpened: state.primaryBar?.isKeepSidebarOpened,
            primaryBarData: state.primaryBar.primaryBarData,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            SecondarySideBar3Type: state.secondaryBar.secondaryBarType3,
            isOnMobile: state.isUserOnMobileDevice.isUserOnMobileDevice,
        };
    });

    const preventSidebarClose = isUserSettingsOpened || isKeepSidebarOpened;

    const dispatch = useDispatch();
    const sidebarConfig = getSidebarConfig();
    const { getPrimarySidebarListItems } = sidebarConfig;
    const {
        ClaimsOverwritten: claimsOverwritten,
        ProductsOverwritten: productsOverwritten,
        ProductsOverwrittenName: productsOverwrittenName,
        HasLicense: hasLicense,
    } = swSettings.components.Home.resources;
    const isViewer = !hasLicense;
    const primarySidebarItems = getPrimarySidebarListItems().map((item) => ({
        ...item,
        isHidden: isViewer ? !ALLOWED_VIEWER_ITEMS.includes(item.id) : item.isHidden,
    }));

    const { username } = swSettings.user;
    const uiVersion = getUiVersion();
    const branch = getUiBranch();
    const { state } = useCurrentStateAndParams();

    const [isSecondaryItemOpen, setIsSecondaryItemOpen] = useState(false);
    const [fadePlacement, setFadePlacement] = useState<EFadePlacement | null>(null);
    const [bottomComponentHeight, setBottomComponentHeight] = useState(0);
    const [userHover, setUserHover] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);

    const topComponentsRef = useRef<HTMLDivElement>(null);
    const bottomComponentsRef = useRef<HTMLDivElement>(null);
    const headerComponentRef = useRef<HTMLDivElement>(null);

    const userHoverRef = useLatestValue<boolean>(userHover);
    const isCursorOverSidebar = useIsCursorOver(sidebarContainerRef);

    //Todo: the scroll position holds the scroll top of the scrollable part(with the help of ref).
    //Todo: I tried to use it in order to remember the scroll position in case the sidebar will be collapsed
    // and we will want the scroll to be as last position when it will be opened again.
    const handleScroll = useCallback(() => {
        if (!topComponentsRef.current) {
            return;
        }

        const { scrollTop, clientHeight, scrollHeight } = topComponentsRef.current;
        if (scrollHeight > clientHeight) {
            if (isPrimarySidebarOpened) {
                setScrollPosition?.(scrollTop);
            }
            if (scrollTop + clientHeight === scrollHeight) {
                setFadePlacement(EFadePlacement.Top);
            } else if (scrollTop === 0) {
                setFadePlacement(EFadePlacement.Bottom);
            } else {
                setFadePlacement(EFadePlacement.TopAndBottom);
            }
        }
    }, [isPrimarySidebarOpened]);
    //Todo: Tried this way to declare the scrollTop as the scrollPosition state.
    if (topComponentsRef?.current?.scrollTop) {
        topComponentsRef.current.scrollTop = scrollPosition;
    }

    useEffect(() => {
        if (bottomComponentsRef.current) {
            setBottomComponentHeight(bottomComponentsRef.current.clientHeight);
        }
        if (topComponentsRef) {
            handleScroll();
        }
    }, [handleScroll]);

    const onSetIsBarOpenChange = useCallback(
        (isOpened: boolean, isClickedOnSecondaryItem?: boolean) => {
            if (!isNil(isClickedOnSecondaryItem)) {
                setIsSecondaryItemOpen(isClickedOnSecondaryItem);
            }

            dispatch(togglePrimarySidebar(isOpened));
        },
        [dispatch],
    );

    const handlePrimarySidebarOpenState = useCallback(() => {
        setUserHover(false);
        if (preventSidebarClose) {
            return;
        }
        if (state.isPrimarySidebarAlwaysOpen) {
            onSetIsBarOpenChange(true);
        }
        if (isSecondaryItemOpen) {
            onSetIsBarOpenChange(true);
        } else if (!isSidebarAlwaysOpenedOnPage(primaryBarData.id) && !state.isPrimarySidebarAlwaysOpen) {
            onSetIsBarOpenChange(false);
        }
    }, [
        isSecondaryItemOpen,
        onSetIsBarOpenChange,
        preventSidebarClose,
        primaryBarData.id,
        state.isPrimarySidebarAlwaysOpen,
    ]);

    useImperativeHandle(ref, () => ({
        onMouseLeavePrimarySidebar: handlePrimarySidebarOpenState,
    }));

    const onSetSelectedPrimarySectionChange = useCallback(
        (state: EPrimarySideBar3StateType, id: EPrimarySideBar3IdType) => {
            dispatch(setPrimarySidebarType(state, id));
        },
        [dispatch],
    );

    //This code initializes the state of the PrimarySidebar on Redux.
    //help the case we are entering through url.
    useEffect(() => {
        if (preventSidebarClose) {
            return;
        }

        let initialState = state.name;

        initialState = initialState === DASHBOARD_NAME_FROM_STATE ? EPrimarySideBar3StateType.Dashboards : initialState;

        const initialSubsectionId = getInitialSubsectionId(initialState, state.parent);

        onSetSelectedPrimarySectionChange(initialState as EPrimarySideBar3StateType, initialSubsectionId);
        if (state.isPrimarySidebarAlwaysOpen) {
            onSetIsBarOpenChange(true);
        }

        const isActuallyHovering = isCursorOverSidebar();
        const currentUserHover = userHoverRef.current;

        if (
            !isSidebarAlwaysOpenedOnPage(initialSubsectionId) &&
            !state.isPrimarySidebarAlwaysOpen &&
            (!currentUserHover || !isActuallyHovering)
        ) {
            if (currentUserHover && !isActuallyHovering) {
                setUserHover(false);
            }
            onSetIsBarOpenChange(false);
        }
    }, [
        state.name,
        preventSidebarClose,
        state.parent,
        state.isPrimarySidebarAlwaysOpen,
        onSetSelectedPrimarySectionChange,
        onSetIsBarOpenChange,
        isCursorOverSidebar,
    ]);

    const onLogoClick = useCallback(() => {
        if (proNavigator.isSalesSolution()) {
            proNavigator.go("salesIntelligence-home");
            return;
        }

        if (isAeoSolution(swSettings)) {
            proNavigator.go("digitalsuite_llm_solutions_homepage");
            return;
        }

        if (isAppSolution(swSettings) && isTrialPlan(swSettings)) {
            redirectToAppsSolution(swSettings);
            return;
        }

        if (swSettings?.components?.Activation?.isAllowed) {
            proNavigator.go(ACTIVATION_HOME_ROUTE);
        } else {
            proNavigator.go(EPrimarySideBar3StateType.Home);
        }

        onSetSelectedPrimarySectionChange(EPrimarySideBar3StateType.Home, EPrimarySideBar3IdType.Homepage);
        onSetIsBarOpenChange(true);
        allTrackers.dispatchTrackEvent("Side Menu", "click", "SWLogo");
    }, [onSetIsBarOpenChange, onSetSelectedPrimarySectionChange]);

    const solutionLogoProps = useMemo(() => getSolutionLogoProps(state), [state]);

    const onComponentItemClick = (trackName: string, itemCounter: number, eventName?: string): void => {
        // for items with special(non-generic) component like "getUserSettingSidebarItemComponent" aka settings components
        const trackingPrimaryItemProps = {
            depth: 1,
            type: "click",
            report: null,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            report_group: null,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            report_domain: null,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            analytics_domain: trackName,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            position_order: itemCounter,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            is_dropdown_list: false,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            is_locked: false,
            tag: "none",
            eventName,
        };
        sidebarTrackingDispatcher(trackingPrimaryItemProps);
    };

    const onBlurPrimarySidebar = () => {
        if (isSecondaryItemOpen) {
            if (!isSidebarAlwaysOpenedOnPage(primaryBarData.id)) {
                onSetIsBarOpenChange(false);
            }
            setIsSecondaryItemOpen(false);
        }
    };

    const sidebarComponents = useMemo(
        () =>
            getSidebarItemsComponents({
                primarySidebarItems,
                onComponentItemClick,
                isPrimarySidebarOpened,
                onSetIsBarOpenChange,
                handlePrimarySidebarOpenState,
                primaryBarData,
                onSetSelectedPrimarySectionChange,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                SecondarySideBar3Type,
            }),
        [
            primarySidebarItems,
            isPrimarySidebarOpened,
            onSetIsBarOpenChange,
            handlePrimarySidebarOpenState,
            primaryBarData,
            onSetSelectedPrimarySectionChange,
            SecondarySideBar3Type,
        ],
    );

    const warningComponents = getWarningComponents({
        forUiVersion: { uiVersion, branch },
        forImpersonate: { username, impersonateMode },
        forPackageDemoTool: { claimsOverwritten, productsOverwritten, productsOverwrittenName },
        isPrimarySidebarOpened,
    });

    const onHoverPrimarySidebar = () => {
        if (!isPrimarySidebarOpened) {
            onSetIsBarOpenChange(true);
        }
        setUserHover(true);
    };

    return (
        <ClickOutsideWrapper onClickOutside={onBlurPrimarySidebar}>
            <PrimarySidebarWrapper
                className="hidden-print"
                onMouseEnter={onHoverPrimarySidebar}
                isCollapsed={!isPrimarySidebarOpened}
            >
                <HeaderWrapper
                    ref={headerComponentRef}
                    isCollapsed={!isPrimarySidebarOpened}
                >
                    <SWLogoWrapper>
                        <SolutionLogo
                            isCollapsed={!isPrimarySidebarOpened}
                            onClick={onLogoClick}
                            logoImagePath={solutionLogoProps.logoImagePath}
                            titleText={solutionLogoProps.titleText}
                        />
                        <SolutionSwitcherDropdown
                            isCollapsed={!isPrimarySidebarOpened}
                            onTrack={() => {
                                onComponentItemClick("Solutions", -1);
                            }}
                        />
                    </SWLogoWrapper>
                    {showQuickSearch ? (
                        <QuickSearch
                            isCollapsed={!isPrimarySidebarOpened}
                            isLocked={isQuickSearchLocked}
                        />
                    ) : null}
                    {cueSubscription ? (
                        <CueSidebarButton
                            isCollapsed={!isPrimarySidebarOpened}
                            cueSubscription={cueSubscription}
                        />
                    ) : null}
                    {showAiStudio ? <AiStudioButton isCollapsed={!isPrimarySidebarOpened} /> : null}
                    {showSuperAgentButton ? <SuperAgentSidebarButton isCollapsed={!isPrimarySidebarOpened} /> : null}
                </HeaderWrapper>
                <ScrollableTopComponentsWrapper
                    ref={topComponentsRef}
                    onScroll={handleScroll}
                    isCollapsed={!isPrimarySidebarOpened}
                    fadePlacement={fadePlacement}
                    bottomComponentHeight={bottomComponentHeight}
                    headerComponentHeight={headerComponentRef.current?.clientHeight ?? DEFAULT_HEADER_HEIGHT}
                >
                    <TopMenuComponentsWrapper isCollapsed={!isPrimarySidebarOpened}>
                        {sidebarComponents.topSidebar.top}
                    </TopMenuComponentsWrapper>
                    <GroupMenuComponentsWrapper isCollapsed={!isPrimarySidebarOpened}>
                        {sidebarComponents.topSidebar.groups}
                    </GroupMenuComponentsWrapper>
                    <BottomMenuComponentsWrapper isCollapsed={!isPrimarySidebarOpened}>
                        {sidebarComponents.topSidebar.bottom}
                    </BottomMenuComponentsWrapper>
                </ScrollableTopComponentsWrapper>
                <BottomComponentsWrapper
                    $hideMargin={isOnMobile}
                    ref={bottomComponentsRef}
                    isCollapsed={!isPrimarySidebarOpened}
                >
                    {sidebarComponents.middleSidebar?.length ? (
                        <SecondaryMiddleItemsWrapper>{sidebarComponents.middleSidebar}</SecondaryMiddleItemsWrapper>
                    ) : null}
                    <WarningsWrapper>{warningComponents}</WarningsWrapper>
                    <SecondaryItemsWrapper>{sidebarComponents.bottomSidebar}</SecondaryItemsWrapper>
                </BottomComponentsWrapper>
            </PrimarySidebarWrapper>
        </ClickOutsideWrapper>
    );
};
export const PrimarySideBar = forwardRef(PrimarySideBarRenderFunction);
